import {
  Input,
  Output,
  EventEmitter,
  Component,
  forwardRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { NgpCheckbox } from 'ng-primitives/checkbox';
import { NgpFocus } from 'ng-primitives/interactions';

import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';

import { SuiFormControlBase } from '@sensika/sui/shared';

@Component({
  selector: 'sui-checkbox',
  standalone: true,
  imports: [NgpCheckbox, NgpFocus],
  templateUrl: './sui-checkbox.component.html',
  styleUrl: './sui-checkbox.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiCheckboxComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiCheckboxComponent),
    },
  ],
  // `data-sui-*` — ours. Every other data-* on a sui host comes from ng-primitives.
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[attr.data-sui-indicator]': 'indicatorPosition' },
})
export class SuiCheckboxComponent
  extends SuiFormControlBase<boolean>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-checkbox-${++SuiCheckboxComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'checkbox';

  @Input() id = '';

  /**
   * `leading` (default) for form contexts; `trailing` right-pins the box for rows
   * in a floating panel, matching `sui-menu-item [selected]` and combobox options.
   */
  @Input() indicatorPosition: 'leading' | 'trailing' = 'leading';

  /**
   * Indeterminate ("mixed") is a visual overlay, not a third value — it's kept
   * separate from the checked boolean that the CVA binds, mirroring the native
   * `<input type="checkbox">.indeterminate` vs `.checked` split.
   */
  @Input() indeterminate = false;
  @Output() indeterminateChange = new EventEmitter<boolean>();

  @Output() checkedChange = new EventEmitter<boolean>();

  @Input()
  set checked(val: boolean) {
    this.value.set(val);
  }

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get isChecked(): boolean {
    return this.value() ?? false;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return !this.value();
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
  }

  onCheckedChange(checked: boolean): void {
    this.setValue(checked);
    this.checkedChange.emit(checked);
  }

  // Keep our input in sync when the primitive clears `mixed` on toggle (its
  // toggle() sets indeterminate=false and emits this) — otherwise the bound
  // input would re-assert the old value and fight the primitive's own state.
  onIndeterminateChange(indeterminate: boolean): void {
    this.indeterminate = indeterminate;
    this.indeterminateChange.emit(indeterminate);
  }
}
